import { useState } from 'react';
import { Calculator, ArrowRight, Info } from 'lucide-react';
import { calculateTax, TaxResult } from '../services/api';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import toast from 'react-hot-toast';

const COUNTRIES = [
    { code: 'CN', label: '中国 (China)' },
    { code: 'US', label: '美国 (United States)' },
    { code: 'DE', label: '德国 (Germany)' },
    { code: 'JP', label: '日本 (Japan)' },
    { code: 'KR', label: '韩国 (South Korea)' },
    { code: 'IN', label: '印度 (India)' },
    { code: 'CA', label: '加拿大 (Canada)' },
    { code: 'AU', label: '澳大利亚 (Australia)' },
    { code: 'GB', label: '英国 (United Kingdom)' },
];

const DEST_COUNTRIES = [
    { code: 'US', label: '美国 (United States)' },
    { code: 'DE', label: '德国/欧盟 (Germany / EU)' },
    { code: 'GB', label: '英国 (United Kingdom)' },
    { code: 'CA', label: '加拿大 (Canada)' },
    { code: 'AU', label: '澳大利亚 (Australia)' },
    { code: 'JP', label: '日本 (Japan)' },
];

// Translate backend breakdown labels to Chinese
const LABEL_ZH: Record<string, string> = {
    'Declared Value': '申报货值',
    'Base Duty': '基础关税',
    'Section 301 Tariff': 'Section 301 附加税',
    'Reciprocal Tariff (2025 EO)': '对等关税（2025 行政令）',
    'Additional Tariff (Fentanyl EO)': '芬太尼附加税',
    'VAT': '增值税',
    'Total Landed Cost': '总到岸成本',
};
const translateLabel = (label: string) => LABEL_ZH[label] ?? label;

const COLORS = ['#6366f1', '#f59e0b', '#10b981', '#ef4444'];

export default function TaxPage() {
    const [form, setForm] = useState({
        hts_code: '',
        declared_value: '',
        origin_country: 'CN',
        destination_country: 'US',
    });
    const [result, setResult] = useState<TaxResult | null>(null);
    const [loading, setLoading] = useState(false);

    const handleCalculate = async () => {
        const value = parseFloat(form.declared_value);
        if (!form.hts_code.trim() || form.hts_code.trim().length < 6) {
            toast.error('请输入有效的 HTS 编码（至少 6 位）');
            return;
        }
        if (!value || value <= 0) {
            toast.error('请输入有效的申报价值');
            return;
        }
        setLoading(true);
        try {
            const data = await calculateTax({
                hts_code: form.hts_code.replace(/[.\s]/g, ''),
                declared_value: value,
                origin_country: form.origin_country,
                destination_country: form.destination_country,
            });
            setResult(data);
        } catch {
            toast.error('计算失败，请检查 HTS 编码后重试');
        } finally {
            setLoading(false);
        }
    };

    const fmt = (n: number) =>
        new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);

    return (
        <div className="p-6 max-w-4xl mx-auto animate-fade-in">
            {/* Header */}
            <div className="mb-8">
                <div className="flex items-center gap-2 mb-2">
                    <Calculator size={20} className="text-brand-400" />
                    <h1 className="text-xl font-bold text-white">税费计算器</h1>
                </div>
                <p className="text-slate-400 text-sm">计算美国进口关税和增值税 (VAT)，含 Section 301 对华加税</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Input Form */}
                <div className="card space-y-4">
                    <p className="section-header">进口信息</p>

                    <div>
                        <label className="text-xs text-slate-400 mb-1 block">HTS 编码 *</label>
                        <input
                            type="text"
                            value={form.hts_code}
                            onChange={(e) => setForm((f) => ({ ...f, hts_code: e.target.value }))}
                            placeholder="例：8471.30.0100 或 8471300100"
                            className="input-field font-mono text-sm"
                        />
                    </div>

                    <div>
                        <label className="text-xs text-slate-400 mb-1 block">申报价值（美元）*</label>
                        <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">$</span>
                            <input
                                type="number"
                                value={form.declared_value}
                                onChange={(e) => setForm((f) => ({ ...f, declared_value: e.target.value }))}
                                placeholder="10000"
                                className="input-field pl-7 text-sm"
                                min="0"
                                step="0.01"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="text-xs text-slate-400 mb-1 block">原产国</label>
                        <select
                            value={form.origin_country}
                            onChange={(e) => setForm((f) => ({ ...f, origin_country: e.target.value }))}
                            className="input-field text-sm"
                        >
                            {COUNTRIES.map((c) => (
                                <option key={c.code} value={c.code}>{c.label}</option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="text-xs text-slate-400 mb-1 block">目的地</label>
                        <select
                            value={form.destination_country}
                            onChange={(e) => setForm((f) => ({ ...f, destination_country: e.target.value }))}
                            className="input-field text-sm"
                        >
                            {DEST_COUNTRIES.map((c) => (
                                <option key={c.code} value={c.code}>{c.label}</option>
                            ))}
                        </select>
                    </div>

                    {form.origin_country === 'CN' && (
                        <div className="flex items-start gap-2 bg-amber-500/10 border border-amber-500/20 rounded-xl p-3 text-xs text-amber-300">
                            <Info size={13} className="flex-shrink-0 mt-0.5" />
                            <p>2025 EO: 中国商品面临 <strong>145%</strong> 综合关税（20% 芬太尼 + 125% 对等 + Section 301）。消费电子类暂时豁免 125% 对等税，仍需缴 45%+。</p>
                        </div>
                    )}

                    <button
                        onClick={handleCalculate}
                        disabled={loading}
                        className="btn-primary w-full justify-center"
                    >
                        {loading ? (
                            <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> 计算中...</>
                        ) : (
                            <><Calculator size={16} /> 计算税费 <ArrowRight size={14} /></>
                        )}
                    </button>
                </div>

                {/* Results */}
                <div className="space-y-4">
                    {result ? (
                        <>
                            {/* Summary card */}
                            <div className="card glow">
                                <p className="section-header">税费明细</p>
                                <div className="space-y-2 mb-4">
                                    {result.breakdown.map((item, i) => (
                                        <div
                                            key={i}
                                            className={`flex justify-between items-center py-1.5 ${translateLabel(item.label) === '总到岸成本'
                                                    ? 'border-t border-white/[0.08] pt-3 mt-1'
                                                    : ''
                                                }`}
                                        >
                                            <div>
                                                <span className={`text-sm ${translateLabel(item.label) === '总到岸成本' ? 'text-white font-semibold' : 'text-slate-400'}`}>
                                                    {translateLabel(item.label)}
                                                </span>
                                                {item.rate != null && item.rate > 0 && (
                                                    <span className="ml-2 text-xs text-slate-600">
                                                        ({(item.rate * 100).toFixed(1)}%)
                                                    </span>
                                                )}
                                            </div>
                                            <span className={`font-mono ${translateLabel(item.label) === '总到岸成本' ? 'text-brand-300 text-lg font-bold' : 'text-slate-300'}`}>
                                                {fmt(item.amount)}
                                            </span>
                                        </div>
                                    ))}
                                </div>

                                {/* Key metrics */}
                                <div className="grid grid-cols-2 gap-3 pt-3 border-t border-white/[0.06]">
                                    <div className="bg-surface-2 rounded-xl p-3">
                                        <p className="text-[10px] text-slate-500 uppercase tracking-wide">实际税率</p>
                                        <p className="text-xl font-bold text-amber-400">{(result.duty_rate * 100).toFixed(1)}%</p>
                                    </div>
                                    <div className="bg-surface-2 rounded-xl p-3">
                                        <p className="text-[10px] text-slate-500 uppercase tracking-wide">总税费</p>
                                        <p className="text-xl font-bold text-red-400">{fmt(result.total_tax)}</p>
                                    </div>
                                </div>
                            </div>

                            {/* Tariff notes */}
                            {result.tariff_notes && result.tariff_notes.length > 0 && (
                                <div className="card space-y-2 border border-amber-500/20 bg-amber-500/5">
                                    <div className="flex items-center gap-2">
                                        <Info size={13} className="text-amber-400 flex-shrink-0" />
                                        <p className="text-xs font-semibold text-amber-300">关税政策说明</p>
                                    </div>
                                    <ul className="space-y-1.5">
                                        {result.tariff_notes.map((note, i) => (
                                            <li key={i} className="text-xs text-amber-200/70 leading-relaxed">
                                                • {note}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {/* Pie chart */}
                            {result.total_tax > 0 && (
                                <div className="card">
                                    <p className="section-header">成本构成</p>
                                    <ResponsiveContainer width="100%" height={200}>
                                        <PieChart>
                                            <Pie
                                                data={[
                                                    { name: '申报货值', value: result.declared_value },
                                                    { name: '关税', value: result.duty_amount },
                                                    { name: '增值税', value: result.vat_amount },
                                                ].filter((d) => d.value > 0)}
                                                cx="50%"
                                                cy="50%"
                                                innerRadius={55}
                                                outerRadius={80}
                                                paddingAngle={3}
                                                dataKey="value"
                                            >
                                                {[0, 1, 2].map((i) => (
                                                    <Cell key={i} fill={COLORS[i]} />
                                                ))}
                                            </Pie>
                                            <Tooltip formatter={(v: number) => fmt(v)} contentStyle={{ background: '#1d1c38', border: '1px solid rgba(99,102,241,0.2)', borderRadius: '12px', color: '#e2e8f0' }} />
                                            <Legend iconType="circle" iconSize={10} formatter={(v) => <span className="text-xs text-slate-400">{v}</span>} />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="card flex flex-col items-center justify-center text-center py-16 text-slate-600">
                            <Calculator size={40} className="mb-3 opacity-30" />
                            <p className="font-medium text-slate-500">请填写进口信息</p>
                            <p className="text-sm mt-1">计算结果将显示在此</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
